"use strict";

exports.__esModule = true;
exports["default"] = void 0;

var _react = _interopRequireDefault(require("react"));

var _propTypes = require("prop-types");

var _ramda = require("ramda");

var _primitives = _interopRequireDefault(require("../../primitives"));

var _container = _interopRequireDefault(require("./container"));

var _glyph = _interopRequireDefault(require("./glyph"));

var _excluded = ["animateGlyph", "children", "color", "darkUi", "data-test-id", "disabled", "fontSize", "fontWeight", "iconSize", "justifyContent", "lineHeight", "letterSpacing", "glyphRight", "glyphLeft", "mb", "onClick", "onMouseDown", "size", "url", "version", "width", "tag", "target"];

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var propTypes = {
  /** A boolean to represent whether the cta right icon should be animated */
  animateGlyph: _propTypes.bool,

  /** Button content, usually a text string, but can be a React component */
  children: _propTypes.node,

  /** A string which represents a color from the color library */
  color: _propTypes.string,

  /** A boolean to set whether the dark version of the cta is used */
  darkUi: _propTypes.bool,

  /** Cypress test id */
  "data-test-id": _propTypes.string,

  /** A boolean to represent whether the cta should be disabled */
  disabled: _propTypes.bool,

  /**
   * Determines font size of children text/components, implementing the style-system.
   * Defaults to a value of 2 for small ctas and a value of 3 otherwise
   */
  fontSize: (0, _propTypes.oneOfType)([_propTypes.array, _propTypes.string, _propTypes.number]),

  /** Array, string, or number for cta font weight */
  fontWeight: (0, _propTypes.oneOfType)([_propTypes.array, _propTypes.string, _propTypes.number]),

  /** Glyph node */
  glyphLeft: _propTypes.node,

  /** Glyph node */
  glyphRight: _propTypes.node,

  /** A number used to override icon size */
  iconSize: _propTypes.number,
  justifyContent: _propTypes.string,

  /** Array, string, or number for cta text letter-spacing */
  letterSpacing: (0, _propTypes.oneOfType)([_propTypes.array, _propTypes.string, _propTypes.number]),

  /** Array, string, or number for cta text line-height */
  lineHeight: (0, _propTypes.oneOfType)([_propTypes.array, _propTypes.string, _propTypes.number]),

  /** Margin bottom, implements style-system */
  mb: (0, _propTypes.oneOfType)([_propTypes.array, _propTypes.string, _propTypes.number]),

  /** Margin right, implements style-system */
  mr: (0, _propTypes.oneOfType)([_propTypes.array, _propTypes.string, _propTypes.number]),

  /** A function that will be called when the button is clicked */
  onClick: _propTypes.func,

  /** A function that will be called on mouse down */
  onMouseDown: _propTypes.func,

  /** Cta size, determines which size styling to apply */
  size: _propTypes.string,

  /**
   * Determines what to render for the CtaContainer.
   * Defaults to an anchor tag if a url prop is present and a <button> tag otherwise
   */
  tag: (0, _propTypes.oneOfType)([_propTypes.node, _propTypes.func]),

  /** Target for cta links, used for opening a new tab or window */
  target: _propTypes.string,

  /** Url provided to CtaContainer for links */
  url: _propTypes.string,

  /** Cta version, determines which version styles to apply */
  version: _propTypes.string,

  /** Determines the width of a Cta using the style-system, defaults to "auto" */
  width: (0, _propTypes.oneOfType)([_propTypes.array, _propTypes.string, _propTypes.number])
};
var defaultProps = {
  animateGlyph: false,
  darkUi: false,
  fontWeight: 2,
  lineHeight: 0,
  mb: 1,
  size: "large",
  version: "secondary"
};
/**
 * The CTA component is the generic button component used throughout the
 * application. It takes a function to execute on click or a url to make
 * it a link. Styles are dictated by size and version and ui. Glyph is same
 * height as text. Defaults to `<button />` tag element unless url is
 * present then uses `<a />`. Extra props are passed through to root
 * element attributes.
 */

var Cta = function Cta(_ref) {
  var animateGlyph = _ref.animateGlyph,
      children = _ref.children,
      color = _ref.color,
      darkUi = _ref.darkUi,
      _ref$dataTestId = _ref["data-test-id"],
      dataTestId = _ref$dataTestId === void 0 ? "cta" : _ref$dataTestId,
      disabled = _ref.disabled,
      fSize = _ref.fontSize,
      fontWeight = _ref.fontWeight,
      iconSize = _ref.iconSize,
      _ref$justifyContent = _ref.justifyContent,
      justifyContent = _ref$justifyContent === void 0 ? "center" : _ref$justifyContent,
      lineHeight = _ref.lineHeight,
      letterSpacing = _ref.letterSpacing,
      glyphRight = _ref.glyphRight,
      glyphLeft = _ref.glyphLeft,
      mb = _ref.mb,
      onClick = _ref.onClick,
      onMouseDown = _ref.onMouseDown,
      size = _ref.size,
      url = _ref.url,
      version = _ref.version,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? "auto" : _ref$width,
      tag = _ref.tag,
      target = _ref.target,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  var defaultSize = size === "small" && version !== "link" ? 2 : 3;
  var fontSize = !(0, _ramda.isNil)(fSize) ? fSize : defaultSize;
  var defaultIconSize = version === "link" ? 16 : 24;
  var tagDefault = url ? "a" : "button";
  return /*#__PURE__*/_react["default"].createElement(_container["default"], _extends({
    color: color,
    "data-test-id": dataTestId,
    disabled: disabled,
    forwardedAs: tag || tagDefault,
    handleClick: disabled ? null : onClick,
    handleMouseDown: disabled ? null : onMouseDown,
    justifyContent: justifyContent,
    mb: mb,
    size: size,
    target: target,
    ui: darkUi ? "dark" : "light",
    url: url,
    version: version,
    width: width
  }, props), /*#__PURE__*/_react["default"].createElement(_primitives["default"].Flex, {
    alignItems: "center",
    as: "span",
    justifyContent: "center",
    lineHeight: 0
  }, glyphLeft && /*#__PURE__*/_react["default"].createElement(_glyph["default"], {
    animateGlyph: false,
    height: iconSize || defaultIconSize,
    mr: 1,
    version: version,
    width: iconSize || defaultIconSize
  }, glyphLeft), children && /*#__PURE__*/_react["default"].createElement(_primitives["default"].Text, {
    color: "inherit",
    fontSize: fontSize,
    fontWeight: fontWeight,
    letterSpacing: letterSpacing,
    lineHeight: lineHeight,
    tag: "p"
  }, children), glyphRight && /*#__PURE__*/_react["default"].createElement(_glyph["default"], {
    animateGlyph: version === "link" ? true : animateGlyph,
    height: iconSize || defaultIconSize,
    ml: 1,
    version: version,
    width: iconSize || defaultIconSize
  }, glyphRight)));
};

Cta.propTypes = propTypes;
Cta.defaultProps = defaultProps;
Cta.displayName = "Modules.Cta";
var _default = Cta;
exports["default"] = _default;